import { createContext, useContext, useState, useEffect } from 'react'
import analytics from '@mobi/libraries/analytics'
import router from 'utils/router'
import storage from 'utils/storage'
import isClient from 'utils/isClient'
import { useLoader } from './useLoader'
import services from 'services'
import { globalHistory } from '@reach/router'
import { navigate } from 'gatsby'
import { addNRPageAction } from 'utils/newRelicEvents'

const BUTTON_NAMES = {
  0: 'Ver opções de pagamento',
  1: 'fechar acordo',
}

const STEPS = ['/ofertas', '/parcelamento', '/conclusao']
const numberOfTotalSteps = STEPS.length
const stepsLastIndex = numberOfTotalSteps - 1

const StepsContext = createContext({})

export const StepsProvider = ({ children }) => {
  const { showLoader, hideLoader } = useLoader()

  const stepsNumberMap = STEPS.reduce((acc, step, index) => {
    acc[index] = step
    return acc
  }, {})

  const stepsNameMap = STEPS.reduce((acc, step, index) => {
    acc[step] = index
    return acc
  }, {})

  const handleStepNumberAndName = (step) => {
    if (typeof step === 'string') {
      return stepsNameMap[step]
    }

    return stepsNumberMap[step]
  }

  const [currentStep, setCurrentStep] = useState(0)

  //* Related to Sender
  const [isButtonNextDisabled, setIsButtonNextDisabled] = useState(false)
  const [buttonName, setButtonName] = useState('')

  // * Related to Groups Selected
  const groupsSelectedSessionStorage = isClient()
    ? storage.session.getItem('groups_selected') || []
    : []

  const [groupsSelected, setGroupsSelected] = useState(
    groupsSelectedSessionStorage
  )

  const sessionId = storage.session.getItem('session_id')
  const debtGroupsData = storage.session.getItem(`${sessionId}-debt-groups`)

  const currentStepQueryString =
    handleStepNumberAndName(router.getPathName()) || 0

  const haveBypass = storage.session.getItem('haveBypass')

  if (haveBypass !== false && currentStepQueryString === 0) {
    const utmSource = !!router.getQueryString('utm_source')
    storage.session.setItem('haveBypass', utmSource)
  }

  useEffect(() => {
    const currentPath = router.getPathName()
    const stepNumber = stepsNameMap[currentPath] || 0
    setCurrentStep(stepNumber)

    return globalHistory.listen(({ location }) => {
      const stepNumber = stepsNameMap[location.pathname] || 0
      setCurrentStep(stepNumber)
    })
  }, [])

  useEffect(() => {
    setButtonName(BUTTON_NAMES[currentStep] || 'próximo')
  }, [currentStep, isButtonNextDisabled])

  useEffect(() => {
    if (currentStep === 0) {
      storage.session.setItem('groups_selected', groupsSelected)

      setIsButtonNextDisabled(groupsSelected?.length === 0)
    }

    if (stepsNumberMap[currentStep] === '/parcelamento') {
      setIsButtonNextDisabled(false)
    }
  }, [isButtonNextDisabled, groupsSelected, currentStep])

  const recoverDebtGroups = async () => {
    try {
      showLoader()

      const sessionId = await services.debt.getSessionId()

      if (!sessionId) {
        navigate('/registro')
      } else if (isClient()) {
        const sessionDebtGroups = await services.debt.getGroupsBySessionId(
          sessionId
        )

        hideLoader()

        const debtGroups = sessionDebtGroups?.debtGroups
        setGroupsSelected(debtGroups)
        return debtGroups
      }

      hideLoader()
    } catch (e) {
      console.error('handleDebtGroups', e)
    }
  }

  if (!debtGroupsData && currentStep > 0) {
    recoverDebtGroups()
  }

  const checkIfGroupIsValid = () => {
    const haveSelectedAllDates = groupsSelectedSessionStorage?.every(
      (item) =>
        item.dueDateSelected.date !== '' && item.dueDateSelected.payment?.id
    )

    setIsButtonNextDisabled(!haveSelectedAllDates)
  }

  const formContinuedPayload = {
    stepName: 'Offers',
    stepType: 'UNKNOWN',
  }

  const nextStep = async ({ callback } = {}) => {
    const nextStepNumber = currentStep + 1
    const pathName = stepsNumberMap[nextStepNumber]

    try {
      if (pathName === '/parcelamento') {
        showLoader('Carregando opções de pagamento...')
        analytics.track('formContinued', formContinuedPayload)

        const initTime = performance.now()
        await navigate(stepsNumberMap[nextStepNumber])
        const endTime = performance.now()

        const timeSpent = endTime - initTime
        addNRPageAction('NextStep - OfertasParcelamento', { timeSpent })

        setCurrentStep(nextStepNumber)

        const timeToHideLoader = timeSpent > 3000 ? 0 : 3000 - timeSpent

        setTimeout(() => {
          hideLoader()
        }, timeToHideLoader)
      } else if (pathName === '/conclusao') {
        showLoader('Concluindo seu acordo...')
        setCurrentStep(nextStepNumber)
        await navigate(stepsNumberMap[nextStepNumber])
      }
    } catch (e) {
      console.error('>> newNextStepError', e)
      setCurrentStep(nextStepNumber)
      await navigate(pathName)
    } finally {
      if (callback) {
        callback(nextStepNumber)
      }
    }
  }

  const prevStep = ({ callback } = {}) => {
    const prevStepNumber = currentStep - 1
    setCurrentStep(prevStepNumber)

    navigate(stepsNumberMap[prevStepNumber])

    if (callback) {
      callback(prevStepNumber)
    }
  }

  return (
    <StepsContext.Provider
      value={{
        currentStep,
        nextStep,
        prevStep,
        isButtonNextDisabled,
        setIsButtonNextDisabled,
        buttonName,
        stepsLastIndex,
        groupsSelected,
        setGroupsSelected,
        checkIfGroupIsValid,
        paymentDays: debtGroupsData?.paymentDays || [],
        minimalPaymentDay: debtGroupsData?.minimalPaymentDay,
        maximalPaymentDay: debtGroupsData?.maximalPaymentDay,
        stepsNameMap,
        stepsNumberMap,
        userName: debtGroupsData?.userName || '',
      }}
    >
      {children}
    </StepsContext.Provider>
  )
}

export const useSteps = () => {
  const context = useContext(StepsContext)

  if (!context) {
    throw new Error(
      `useSteps hook must be used within a 'StepsProvider' component`
    )
  }

  return context
}
